import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatService } from '../services/chat/chat.services';
import { useChatStore } from '../utils/chatStore';

export interface AgentChatContext {
  current_page?: string;
  step_type?: string;
  lesson_id?: number;
  topic?: string;
  level_id?: number;
  metadata?: Record<string, any>;
}

export const useAgentChat = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { addUserMessage, addAssistantMessage, getFormattedHistory } = useChatStore();
  const chatService = new ChatService();
  const navigate = useNavigate();

  const talk = async (params: {
    message: string;
    context?: AgentChatContext;
    session_id?: string;
    skipAddUserMessage?: boolean;
  }) => {
    setIsLoading(true);
    try {
      // 1. If there's a user message, save it to the store
      if (params.message && !params.skipAddUserMessage) {
        addUserMessage(params.message);
      }

      // 2. Fetch last 20 messages from conversation history to support 10-20 history context
      const history = getFormattedHistory(20);

      // 3. Send structured payload to agent/chat endpoint
      const response = await chatService.TALKWITHAGENT_V2({
        message: params.message,
        context: params.context,
        history: history,
        session_id: params.session_id,
      });

      if (response) {
        const { message: responseMessage, client_action, data } = response;

        // 4. Save Assistant response to the store
        if (responseMessage) {
          addAssistantMessage(responseMessage, false, data);
        }

        // 5. Intercept and execute client action frames (e.g. navigation)
        if (client_action && client_action.type === 'navigate' && client_action.route) {
          setTimeout(() => {
            navigate(client_action.route, { state: client_action.state });
          }, 1500); // 1.5s delay to allow student to read the Hebrew redirect message
        }

        return response;
      }

      return null;
    } catch (error) {
      console.error('Agent chat interaction failed:', error);
      addAssistantMessage('COMMUNICATION_ERROR', true);
      return { message: 'COMMUNICATION_ERROR', isError: true };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    talk,
  };
};
